/**
 * Turning one school's title list into a Subject × Class grid.
 *
 * <p>The axes come from the catalogue, not from the masters. `BOOK_CLASSES` allows
 * fifteen classes and the Subject master nineteen subjects, which bounds the worst
 * case at 285 titles — but a publisher's actual catalogue is a fraction of that, and a
 * grid that drew every allowed combination would be mostly rows about books nobody has
 * written. So a subject or a class earns its line by having at least one title in it.
 *
 * <p>Three cell states, and the third is why this shape is worth having. A cell with
 * no title at all is *not* an unticked box: there is no Class 9 Art and a grid that
 * cannot say so makes "they have not taken it" and "it does not exist" look identical,
 * which is exactly what the old School × Title grid did.
 *
 * <p>A cell can hold more than one title — two Class 8 Maths books is a normal thing
 * for a publisher to have — so a cell is a list, and its state is none, some or all.
 *
 * <p>Pure and dependency-free: the ordering, the counting and the three-state rule are
 * all things that look right and are wrong, so they are run rather than reasoned about.
 */

/** A cell with nothing in it: the catalogue has no such book. */
export const ABSENT = 'ABSENT';
/** Titles here, none of them taken. */
export const NONE = 'NONE';
/** Some taken, some not — only reachable where a cell holds more than one title. */
export const SOME = 'SOME';
export const ALL = 'ALL';

const UNFILED = 'Unfiled';
const UNCLASSED = 'No class';

const valueOf = (title, key, blank) => title?.[key]?.trim() || blank;

/**
 * Orders by a canonical list, with anything the list does not name after it.
 *
 * <p>Classes have to come off `BOOK_CLASSES` rather than be sorted: Nursery, LKG and
 * UKG come before Class 1 and sort nowhere near it, and "Class 10" lands between
 * Class 1 and Class 2 under a plain string sort. Subjects have no canonical order, so
 * they are alphabetical.
 */
function orderBy(canonical, blank) {
  const rank = new Map((canonical ?? []).map((value, index) => [value, index]));
  return (a, b) => {
    if (a === b) return 0;
    if (a === blank) return 1;
    if (b === blank) return -1;
    const ra = rank.has(a) ? rank.get(a) : Number.MAX_SAFE_INTEGER;
    const rb = rank.has(b) ? rank.get(b) : Number.MAX_SAFE_INTEGER;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, undefined, { numeric: true });
  };
}

/**
 * @param titles rows of `AdoptionDtos.AdoptedTitle` — subject, classLevel, adopted
 * @param classOrder the Book Classes master, so the columns run Nursery → Class 12
 * @returns {subjects, classes, cellAt, rows, columns, adopted, available}
 */
export function buildGrid(titles, classOrder = []) {
  const all = titles ?? [];
  const cells = new Map();
  const subjects = new Set();
  const classes = new Set();

  for (const title of all) {
    const subject = valueOf(title, 'subject', UNFILED);
    const classLevel = valueOf(title, 'classLevel', UNCLASSED);
    subjects.add(subject);
    classes.add(classLevel);
    const key = `${subject}\u0000${classLevel}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(title);
  }

  const subjectList = [...subjects].sort(orderBy(null, UNFILED));
  const classList = [...classes].sort(orderBy(classOrder, UNCLASSED));

  const at = (subject, classLevel) => cells.get(`${subject}\u0000${classLevel}`) ?? [];

  /** A line's titles and how many of them are taken — a row, a column or the lot. */
  const tally = (list) => {
    const adopted = list.filter((title) => title.adopted).length;
    return {
      titles: list,
      titleIds: list.map((title) => title.titleId),
      available: list.length,
      adopted,
      // What a click on this line should do: take it unless it is already all taken.
      nextAdopted: adopted < list.length,
    };
  };

  return {
    subjects: subjectList,
    classes: classList,

    /** One cell: its titles, its state, and what a click would set them to. */
    cellAt(subject, classLevel) {
      const list = at(subject, classLevel);
      if (list.length === 0) {
        return { state: ABSENT, titles: [], titleIds: [], adopted: 0, available: 0 };
      }
      const counted = tally(list);
      return {
        ...counted,
        state: counted.adopted === 0 ? NONE : counted.adopted === list.length ? ALL : SOME,
      };
    },

    rowAt: (subject) => tally(all.filter((t) => valueOf(t, 'subject', UNFILED) === subject)),
    columnAt: (classLevel) =>
      tally(all.filter((t) => valueOf(t, 'classLevel', UNCLASSED) === classLevel)),

    available: all.length,
    adopted: all.filter((title) => title.adopted).length,
  };
}
